import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import { IntentService } from "../../services/IntentService";
import { IntentGroup } from "../../model_gen/intent";
import { Card, CardContent } from "../ui/card";
import IntentGroupsTable from "./IntentGroupsTable";

interface SortConfig {
  key: keyof IntentGroup | null;
  direction: "asc" | "desc";
}

const IntentGroupsOverview: React.FC = () => {
  const navigate = useNavigate();
  const { apiKey, companyId } = usePermissions();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalIntentGroups, setTotalIntentGroups] = useState(0);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: "desc" });
  const pageSize = 10;
  const [intentGroupsByPage, setIntentGroupsByPage] = useState<Record<number, IntentGroup[]>>({});

  const loadIntentGroups = useCallback(() => {
    if (!apiKey || !companyId) return;

    if (!intentGroupsByPage[currentPage]) {
      IntentService.getIntentGroups(
        (currentPage - 1) * pageSize,
        pageSize,
        setIsLoading,
        apiKey
      ).subscribe(
        (response: any) => {
          setIntentGroupsByPage(prev => ({
            ...prev,
            [currentPage]: response.intent_groups
          }));
          setTotalIntentGroups(response.total);
          setIsLoading(false);
        },
        (error: any) => {
          console.error("Error loading intent groups:", error);
          setIsLoading(false);
        }
      );
    }
  }, [apiKey, companyId, currentPage, pageSize, intentGroupsByPage]);

  useEffect(() => {
    loadIntentGroups();
  }, [loadIntentGroups]);

  const handleSort = (key: keyof IntentGroup) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedIntentGroups = React.useMemo(() => {
    const currentGroups = intentGroupsByPage[currentPage] || [];
    if (!sortConfig.key) return currentGroups;
    
    return [...currentGroups].sort((a, b) => {
      const aValue = a[sortConfig.key!];
      const bValue = b[sortConfig.key!];
      
      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [intentGroupsByPage, currentPage, sortConfig]);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Home
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">Intent Groups</span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="space-y-1">
              <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                Intent Groups
              </h1>
              <p className="text-lg text-muted-foreground font-gc-reg">
                Manage your intent groups across applications
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Intent Groups Table */}
        <IntentGroupsTable
          intentGroups={sortedIntentGroups}
          loading={isLoading && (!intentGroupsByPage[currentPage] || intentGroupsByPage[currentPage].length === 0)}
          sortConfig={sortConfig}
          onSort={handleSort}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          pageSize={pageSize}
          totalItems={totalIntentGroups}
        />
      </div>
    </div>
  );
};

export default IntentGroupsOverview;